import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Send, Loader2, MessageSquarePlus, MessageSquare, Paperclip, ChevronDown,
  Archive, Pencil, Check, X, Search, ShieldCheck, ShieldAlert, Sparkles,
  FileSpreadsheet, MessageSquareText, FileSearch, CheckCheck, PenLine,
} from "lucide-react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { toast } from "sonner";
import { detectSensitiveData } from "../lib/sensitiveData";
import {
  ChatMessage, TypingIndicator, WELCOME_MESSAGE as WELCOME,
} from "../components/ai/ChatPrimitives";
import { useFeatures } from "../context/FeaturesContext";
import { useAuth } from "../context/AuthContext";

const fmtRelative = (iso) => {
  if (!iso) return "";
  const sec = Math.floor((new Date() - new Date(iso)) / 1000);
  if (sec < 60) return "ora";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min fa`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h fa`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} g fa`;
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const MODEL_DOTS = { claude: "#D97757", gemini: "#4E86F5" };

const TOOL_LINKS = [
  { to: "/assistant/reconcile", label: "Riconcilia due file", icon: FileSpreadsheet, testId: "assistant-tool-reconcile" },
  { to: "/assistant/ask-file", label: "Chiedi al tuo file", icon: MessageSquareText, testId: "assistant-tool-askfile" },
  { to: "/assistant/analyze-doc", label: "Analizza documento", icon: FileSearch, testId: "assistant-tool-analyzedoc" },
  { to: "/assistant/quality", label: "Controlli qualità", icon: CheckCheck, testId: "assistant-tool-quality" },
  { to: "/assistant/drafts", label: "Bozze e comunicazioni", icon: PenLine, testId: "assistant-tool-drafts" },
];

/* ------------------------------ Model picker ----------------------------- */
const ModelPicker = ({ models, model, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const current = models.find((m) => m.key === model) || models[0];
  if (!current) return null;
  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled}
              onClick={() => models.length > 1 && setOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-border bg-subtle hover:border-border-strong text-text-secondary transition-colors disabled:opacity-50"
              aria-haspopup="listbox" aria-expanded={open} data-testid="chat-model-picker">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: MODEL_DOTS[current.key] || "#F4F430" }} aria-hidden="true" />
        {current.label}
        {models.length > 1 && <ChevronDown size={12} aria-hidden="true" />}
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1.5 min-w-[180px] rounded-lg border border-border bg-surface shadow-lg py-1 z-30" role="listbox">
          {models.map((m) => (
            <button key={m.key} type="button" role="option" aria-selected={m.key === model}
                    onClick={() => { onChange(m.key); setOpen(false); }}
                    className={`w-full flex items-center gap-2 text-left text-xs px-3 py-2 hover:bg-subtle ${m.key === model ? "font-medium text-text-primary" : "text-text-secondary"}`}
                    data-testid={`chat-model-${m.key}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: MODEL_DOTS[m.key] || "#F4F430" }} aria-hidden="true" />
              {m.label}
              {m.key === model && <Check size={12} className="ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------------------- Conversation list -------------------------- */
const ConversationItem = ({ conv, active, onSelect, onRename, onArchive }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(conv.title);

  const submit = async () => {
    const next = draft.trim();
    if (!next || next === conv.title) {
      setEditing(false);
      setDraft(conv.title);
      return;
    }
    try {
      await onRename(conv.id, next);
      setEditing(false);
    } catch {
      setDraft(conv.title);
    }
  };

  return (
    <div
      data-testid={`conv-${conv.id}`}
      className={`group rounded-md px-3 py-2.5 cursor-pointer transition-colors ${
        active ? "bg-accent-subtle border border-accent/30" : "hover:bg-subtle border border-transparent"
      }`}
      onClick={() => !editing && onSelect(conv.id)}
    >
      {editing ? (
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") { setEditing(false); setDraft(conv.title); }
            }}
            className="flex-1 min-w-0 h-7 text-xs bg-surface border border-border rounded px-2 focus:outline-none focus:border-accent"
            maxLength={120}
            data-testid={`conv-${conv.id}-edit`}
          />
          <button onClick={submit} className="text-success hover:bg-subtle p-1 rounded"><Check size={12} /></button>
          <button onClick={() => { setEditing(false); setDraft(conv.title); }} className="text-text-muted hover:bg-subtle p-1 rounded"><X size={12} /></button>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <MessageSquare size={13} className="text-text-muted mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate text-text-primary">{conv.title}</div>
            <div className="text-[10px] text-text-muted mt-0.5 flex items-center gap-2">
              <span>{fmtRelative(conv.last_message_at)}</span>
              <span>·</span>
              <span>{conv.message_count} msg</span>
            </div>
          </div>
          <div className="hidden group-hover:flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-surface text-text-muted hover:text-text-primary" title="Rinomina" data-testid={`conv-${conv.id}-rename`}>
              <Pencil size={11} />
            </button>
            <button
              onClick={() => onArchive(conv)}
              className="p-1 rounded hover:bg-surface text-text-muted hover:text-text-primary"
              title="Archivia (resta nello storico aziendale)"
              data-testid={`conv-${conv.id}-delete`}
            >
              <Archive size={11} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* --------------------------------- Page ---------------------------------- */
export default function Assistant() {
  const location = useLocation();
  const { user } = useAuth();
  const { features } = useFeatures();
  const showAiTools = user?.role === "super_admin" || (features && features.ai_tools);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [filter, setFilter] = useState("");
  const [retentionMonths, setRetentionMonths] = useState(6);
  const [pendingWarning, setPendingWarning] = useState(null); // { text, hits }
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileRef = useRef(null);

  const [listFailed, setListFailed] = useState(false);
  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/ai/conversations");
      setConversations(data.items || []);
      setListFailed(false);
      if (data.retention_months) setRetentionMonths(data.retention_months);
    } catch (e) {
      if (e?.response?.status !== 403) setListFailed(true);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Modelli abilitati per l'org (assegnati dall'admin)
  useEffect(() => {
    api.get("/ai/models").then(({ data }) => {
      setModels(data.models || []);
      const preferred = location?.state?.model;
      const keys = (data.models || []).map((m) => m.key);
      setModel(preferred && keys.includes(preferred) ? preferred : data.default_model);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link da Dashboard widget: se location.state.conversationId è valorizzato,
  // apri quella conversazione in automatico al primo render
  useEffect(() => {
    const target = location?.state?.conversationId;
    if (target && target !== activeConvId) {
      openConversation(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.conversationId]);

  // Deep-link da Dashboard AI panel: precompila l'input con il testo scritto
  useEffect(() => {
    const im = location?.state?.initialMessage;
    if (im) {
      setInput(im);
      textareaRef.current?.focus();
    }
    // Deep-link da Documenti ("Chiedi all'AI"): allegato precaricato
    const ia = location?.state?.initialAttachment;
    if (ia?.id) {
      setAttachments((a) => (a.some((x) => x.id === ia.id) ? a : [...a, ia]));
      textareaRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    // Auto-scroll al fondo (modifica DOM, non setState)
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openConversation = async (convId) => {
    if (convId === activeConvId) return;
    setActiveConvId(convId);
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/ai/conversations/${convId}/messages`);
      const msgs = (data.items || []).map((m) => ({
        role: m.role,
        content: m.content,
        tools_used: m.tools_used || [],
        action_meta: m.action_meta || null,
        kb_sources: m.kb_sources || [],
        web_sources: m.web_sources || [],
        market_boxes: m.market_boxes || [],
        attachments: m.attachments || [],
        model_key: m.model_key || null,
      }));
      setMessages([WELCOME, ...msgs]);
    } catch (e) {
      toast.error(`Errore caricamento messaggi: ${e?.response?.data?.detail || e?.message}`);
      setMessages([WELCOME]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const startNewChat = () => {
    setActiveConvId(null);
    setMessages([WELCOME]);
    setInput("");
    setAttachments([]);
    textareaRef.current?.focus();
  };

  /* ------------------------------ Allegati ------------------------------- */
  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (attachments.length >= 3) {
      toast.error("Massimo 3 allegati per messaggio.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10 MB).");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/ai/chat/attachments", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito.");
    } finally {
      setUploading(false);
    }
  };

  /* -------------------------------- Invio -------------------------------- */
  const onSend = () => {
    const text = input.trim();
    if (!text || sending) return;
    const hits = detectSensitiveData(text);
    if (hits.length > 0) {
      setPendingWarning({ text, hits });
      return;
    }
    doSend(text, null);
  };

  const doSend = async (text, sensitiveTypes) => {
    setPendingWarning(null);
    const sentAttachments = attachments;
    setMessages((m) => [...m,
      { role: "user", content: text, attachments: sentAttachments },
      { role: "assistant", content: "", streaming: true, model_key: model },
    ]);
    setInput("");
    setAttachments([]);
    setSending(true);

    const replaceLast = (patch) => setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch };
      return copy;
    });

    try {
      const payload = { message: text, model };
      if (activeConvId) payload.conversation_id = activeConvId;
      if (sensitiveTypes && sensitiveTypes.length > 0) payload.sensitive_warning = sensitiveTypes;
      if (sentAttachments.length > 0) payload.attachment_ids = sentAttachments.map((a) => a.id);

      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("wattline_token")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let detail = `Errore ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finished = false;
      while (!finished) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5)); } catch { continue; }
          if (ev.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, content: (last.content || "") + ev.content, tool_status: null };
              return copy;
            });
          } else if (ev.type === "tool") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], tool_status: ev.label || "Consulto uno strumento…" };
              return copy;
            });
          } else if (ev.type === "done") {
            finished = true;
            if (ev.conversation_id) setActiveConvId((cur) => cur || ev.conversation_id);
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = {
                ...last,
                streaming: false,
                tool_status: null,
                content: ev.message || last.content,
                kb_sources: ev.kb_sources || [],
                web_sources: ev.web_sources || [],
                market_boxes: ev.market_boxes || [],
                action_meta: ev.action_meta || null,
                model_key: ev.model_key || last.model_key,
                tools_used: ev.tools_used || [],
              };
              return copy;
            });
          } else if (ev.type === "error") {
            throw new Error(ev.detail || "Errore");
          }
        }
      }
      loadConversations();
    } catch (e) {
      const detail = e?.message || "Errore";
      toast.error(`Errore assistente AI: ${detail}`);
      replaceLast({ streaming: false, content: `⚠️ Errore: ${detail}` });
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const renameConversation = async (convId, title) => {
    try {
      const { data } = await api.patch(`/ai/conversations/${convId}`, { title });
      setConversations((cs) => cs.map((c) => (c.id === convId ? data : c)));
      toast.success("Titolo aggiornato");
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || e?.message}`);
      throw e;
    }
  };

  const archiveConversation = async (convId) => {
    try {
      await api.delete(`/ai/conversations/${convId}`);
      setConversations((cs) => cs.filter((c) => c.id !== convId));
      if (activeConvId === convId) startNewChat();
      toast.success("Conversazione archiviata", {
        description: "Resta nello storico aziendale fino alla scadenza della conservazione.",
      });
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setArchiveTarget(null);
    }
  };

  const filtered = filter.trim()
    ? conversations.filter((c) => c.title.toLowerCase().includes(filter.toLowerCase()))
    : conversations;

  if (!showAiTools) {
    return (
      <div className="wl-page">
        <div className="bg-surface border border-border rounded-xl p-10 text-center max-w-lg mx-auto mt-12"
             data-testid="assistant-not-enabled">
          <Sparkles size={30} strokeWidth={1.5} className="mx-auto mb-3" style={{ color: "var(--wl-text-muted)" }} />
          <div className="text-base font-medium" style={{ color: "var(--wl-ink)" }}>AI aziendale non attiva</div>
          <div className="text-sm mt-2" style={{ color: "var(--wl-text-secondary)" }}>
            Questo modulo non è incluso nel piano della tua azienda. Contatta Wattline per attivarlo.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]" data-testid="assistant-page">
      {/* Sidebar conversazioni */}
      <aside className="w-72 border-r border-border bg-surface flex-col flex-shrink-0 hidden md:flex">
        <div className="p-3 border-b border-border space-y-2">
          <button
            type="button"
            onClick={startNewChat}
            data-testid="btn-new-chat"
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-brand hover:bg-brand-hover text-white text-sm font-medium transition-colors"
          >
            <MessageSquarePlus size={14} />
            Nuova conversazione
          </button>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Cerca…"
              data-testid="conv-search"
              className="w-full pl-7 pr-2 h-8 text-xs bg-subtle border border-border rounded focus:outline-none focus:border-accent"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="conv-list">
          {loadingList ? (
            <div className="flex justify-center py-6 text-text-muted text-xs">
              <Loader2 className="animate-spin" size={14} />
            </div>
          ) : listFailed ? (
            <LoadErrorBox compact what="le conversazioni" onRetry={loadConversations} testId="assistant-list-error" />
          ) : filtered.length === 0 ? (
            <div className="text-center text-xs text-text-muted py-6 px-3">
              {filter ? "Nessuna conversazione corrisponde." : "Nessuna conversazione ancora. Avvia la prima dall'input qui a destra."}
            </div>
          ) : (
            filtered.map((c) => (
              <ConversationItem
                key={c.id}
                conv={c}
                active={c.id === activeConvId}
                onSelect={openConversation}
                onRename={renameConversation}
                onArchive={setArchiveTarget}
              />
            ))
          )}
        </div>
        <div className="px-3 py-2 border-t border-border text-[10px] text-text-muted text-center">
          Conservazione {retentionMonths} mesi · poi eliminazione automatica
        </div>
      </aside>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 md:px-8 pt-4 pb-2 flex-shrink-0 border-b border-border">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-lg font-semibold tracking-tight text-text-primary">AI aziendale</h1>
              <p className="text-xs text-text-secondary mt-0.5 hidden sm:block">
                Chiedi quello che ti serve senza uscire dalla piattaforma: i tuoi dati restano qui.
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="assistant-tools-row">
              {TOOL_LINKS.map((t) => (
                <Link key={t.to} to={t.to} data-testid={t.testId}
                      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full border border-border bg-surface hover:border-accent text-text-secondary hover:text-text-primary transition-colors">
                  <t.icon size={11} aria-hidden="true" />
                  {t.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 pb-2" data-testid="messages-container">
          <div className="max-w-4xl mx-auto">
            {loadingMessages ? (
              <div className="flex justify-center py-12 text-text-muted">
                <Loader2 className="animate-spin" size={18} />
              </div>
            ) : (
              messages.map((m, i) => <ChatMessage key={i} msg={m} />)
            )}
            {sending && messages[messages.length - 1]?.streaming && !messages[messages.length - 1]?.content && (
              <TypingIndicator />
            )}
          </div>
        </div>

        <div className="border-t border-border bg-surface px-4 md:px-8 py-4 flex-shrink-0">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-1.5 mb-2 text-[11px] text-text-muted" data-testid="ai-transparency-notice">
              <ShieldCheck size={12} className="flex-shrink-0" style={{ color: "var(--wl-accent-strong, #0f766e)" }} />
              <span>Le conversazioni sono conservate e visibili all&apos;amministratore dell&apos;azienda.</span>
            </div>

            <div className="rounded-xl border border-border bg-surface focus-within:ring-2 focus-within:ring-accent/30 focus-within:border-accent transition-all">
              {attachments.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap px-3 pt-2.5" data-testid="chat-attachments-row">
                  {attachments.map((a) => (
                    <span key={a.id}
                          className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-subtle border border-border text-text-secondary max-w-[220px]"
                          data-testid={`chat-attachment-${a.id}`}>
                      <Paperclip size={10} className="flex-shrink-0" />
                      <span className="truncate">{a.filename}</span>
                      <button type="button" onClick={() => setAttachments((x) => x.filter((y) => y.id !== a.id))}
                              className="hover:text-danger flex-shrink-0" aria-label={`Rimuovi ${a.filename}`}
                              data-testid={`chat-attachment-remove-${a.id}`}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Scrivi un messaggio… (Invio per inviare, Shift+Invio per andare a capo)"
                rows={2}
                maxLength={20000}
                data-testid="chat-input"
                disabled={sending}
                className="w-full resize-none bg-transparent px-4 py-3 text-sm leading-relaxed focus:outline-none disabled:opacity-60"
              />
              <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5">
                <div className="flex items-center gap-1.5">
                  <input ref={fileRef} type="file" className="hidden"
                         accept=".pdf,.docx,.txt,.md,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp"
                         onChange={onPickFile} data-testid="chat-file-input" />
                  <button type="button" onClick={() => fileRef.current?.click()}
                          disabled={sending || uploading || attachments.length >= 3}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-subtle hover:border-border-strong text-text-secondary transition-colors disabled:opacity-50"
                          title="Allega un file (PDF, Excel, CSV, Word, TXT, immagini)"
                          aria-label="Allega un file" data-testid="chat-attach-btn">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                  </button>
                  {models.length > 0 && model && (
                    <ModelPicker models={models} model={model} onChange={setModel} disabled={sending} />
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-text-muted hidden sm:inline">{input.length} / 20000</span>
                  <button
                    type="button"
                    onClick={onSend}
                    disabled={!input.trim() || sending}
                    data-testid="chat-send"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand hover:bg-brand-hover text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-2 text-[11px] text-text-muted">
              <span>
                {activeConvId
                  ? "Conversazione attiva"
                  : <span className="text-accent">Nuova conversazione (salvata al primo invio)</span>}
              </span>
              <span className="md:hidden">Conservazione {retentionMonths} mesi</span>
            </div>
          </div>
        </div>
      </div>

      {archiveTarget && (
        <div className="wl-modal-overlay" onClick={() => setArchiveTarget(null)}>
          <div className="wl-modal wl-modal--sm" role="dialog" aria-modal="true"
               onClick={(e) => e.stopPropagation()} data-testid="archive-confirm-modal">
            <div className="wl-modal__header">
              <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true">
                <Archive size={18} />
              </div>
              <div className="wl-modal__title-block">
                <h3 className="wl-modal__title">Archiviare &quot;{archiveTarget.title}&quot;?</h3>
                <p className="wl-modal__desc">
                  Archiviare non è cancellare: la conversazione sparisce dalla tua lista ma resta
                  nello storico aziendale visibile all&apos;amministratore, fino alla scadenza della
                  conservazione ({retentionMonths} mesi).
                </p>
              </div>
            </div>
            <div className="wl-modal__actions">
              <button type="button" onClick={() => setArchiveTarget(null)}
                      className="wl-btn wl-btn--ghost" data-testid="archive-cancel">
                Annulla
              </button>
              <button type="button" onClick={() => archiveConversation(archiveTarget.id)}
                      className="wl-btn wl-btn--primary" data-testid="archive-confirm">
                Archivia
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingWarning && (
        <div className="wl-modal-overlay" onClick={() => setPendingWarning(null)}>
          <div className="wl-modal wl-modal--sm" role="dialog" aria-modal="true"
               onClick={(e) => e.stopPropagation()} data-testid="sensitive-warning-modal">
            <div className="wl-modal__header">
              <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true"
                   style={{ color: "var(--wl-warning, #b45309)" }}>
                <ShieldAlert size={18} />
              </div>
              <div className="wl-modal__title-block">
                <h3 className="wl-modal__title">Attenzione: stai inserendo dati personali. Continuare?</h3>
                <p className="wl-modal__desc">Nel messaggio sembrano esserci:</p>
              </div>
            </div>
            <div className="wl-modal__body">
              <ul className="space-y-1.5">
                {pendingWarning.hits.map((h) => (
                  <li key={h.id} className="flex items-center gap-2 text-sm" style={{ color: "var(--wl-ink)" }}
                      data-testid={`sensitive-hit-${h.id}`}>
                    <ShieldAlert size={13} className="flex-shrink-0" style={{ color: "var(--wl-warning, #b45309)" }} />
                    {h.label}
                  </li>
                ))}
              </ul>
              <p className="text-xs mt-3" style={{ color: "var(--wl-text-secondary)" }}>
                Puoi comunque procedere. Verrà registrato solo il tipo di dato rilevato (mai il contenuto),
                visibile all&apos;amministratore nella vista &quot;Uso dell&apos;AI&quot;.
              </p>
            </div>
            <div className="wl-modal__actions">
              <button type="button" onClick={() => setPendingWarning(null)}
                      className="wl-btn wl-btn--ghost" data-testid="sensitive-cancel">
                Annulla
              </button>
              <button type="button"
                      onClick={() => doSend(pendingWarning.text, pendingWarning.hits.map((h) => h.id))}
                      className="wl-btn wl-btn--primary" data-testid="sensitive-continue">
                Continua comunque
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
